/**
 * `WikilinkNode` — Notion-style inline note links for the rich editor (2026-06-23).
 *
 * Renders a `[[Note Title]]` wikilink as a clean, clickable inline chip (the title, no visible
 * `[[ ]]` syntax), styled resolved vs unresolved, that navigates to the target note on click.
 * The canonical markdown body stays byte-stable: this node serializes back to `[[Title]]` via the
 * turndown rule in `markdownRoundtrip.ts`, and `[[Title]]` parses into this node via the marked
 * extension there — so notes on disk and the backend's wikilink resolver are unaffected.
 *
 * Resolution is title→noteId over the cached title set (the same `note_titles` data the
 * autocomplete uses), resolved live at click time so a link works as soon as the title list loads.
 * The resolved id is also captured onto the node (`noteId` attr) at insert time for
 * rename-resilient navigation within a session; it is intentionally NOT serialized to markdown
 * (the persisted form is the portable `[[Title]]`).
 *
 * An input rule converts a manually-typed `[[Title]]` into the chip the moment the closing `]]`
 * is typed, so typing and the `[[` autocomplete both produce the same node.
 */

import { Node, mergeAttributes, nodeInputRule } from '@tiptap/core';

export interface WikilinkOptions {
  /** Resolve a wikilink title to a target note id, or null if no live note matches. */
  resolveTitle: (title: string) => string | null;
  /** Navigate to a resolved note id (wired to React Router in RichTextEditorPane). */
  onNavigate: (noteId: string) => void;
  /** Fetch a short preview (title + body snippet) for the hover card. Undefined → no card. */
  getPreview?: (noteId: string) => Promise<{ title: string; snippet: string } | null>;
  /** Subscribe to title-cache refreshes so a chip re-resolves resolved/unresolved state when the
   *  title list loads (it's fetched async on editor mount). Returns an unsubscribe thunk. */
  subscribe?: (cb: () => void) => () => void;
}

/** Matches a complete `[[title]]` — the inner text is the target title. */
const WIKILINK_INPUT_RULE = /\[\[([^\]\n]+)\]\]$/;

export const WikilinkNode = Node.create<WikilinkOptions>({
  name: 'wikilink',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return {
      resolveTitle: () => null,
      onNavigate: () => {},
    };
  },

  addAttributes() {
    return {
      title: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-title') ?? el.textContent ?? '',
        renderHTML: (attrs) => ({ 'data-title': attrs.title }),
      },
      // Captured at insert/parse time for rename-resilient navigation; NOT serialized to
      // markdown (turndown emits `[[Title]]` only). Best-effort — re-resolved by title on click.
      noteId: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-note-id'),
        renderHTML: (attrs) => (attrs.noteId ? { 'data-note-id': attrs.noteId } : {}),
      },
    };
  },

  parseHTML() {
    // Marked emits `<a class="wikilink" data-title="…">` for `[[Title]]`; the rich-editor paste
    // path also recognizes a pre-rendered chip. High priority so this NODE rule claims the
    // anchor before the Link MARK rule (`a[href]`) can wrap it.
    return [{ tag: 'a.wikilink', priority: 100 }];
  },

  renderHTML({ HTMLAttributes, node }) {
    const title = String(node.attrs.title ?? '');
    return [
      'a',
      mergeAttributes(HTMLAttributes, { class: 'wikilink', href: '#wikilink' }),
      title,
    ];
  },

  addNodeView() {
    return ({ node }) => {
      const options = this.options;
      const title = String(node.attrs.title ?? '');

      const dom = document.createElement('a');
      dom.className = 'wikilink';
      dom.href = '#wikilink';
      dom.dataset.title = title;
      dom.textContent = title;

      const applyResolution = (): string | null => {
        // Prefer the id captured on the node (rename-proof this session); else resolve by title.
        const captured = typeof node.attrs.noteId === 'string' ? node.attrs.noteId : null;
        const resolved = captured ?? options.resolveTitle(title);
        if (resolved) {
          dom.classList.remove('wikilink--unresolved');
          dom.title = `Open "${title}"`;
        } else {
          dom.classList.add('wikilink--unresolved');
          dom.title = `No note named "${title}" yet — it will link once that note exists`;
        }
        return resolved;
      };
      applyResolution();
      // Re-resolve when the title cache refreshes (loads async on mount), so a link that rendered
      // before the titles arrived flips from "unresolved" to "resolved" without needing an edit.
      const unsubscribe =
        typeof options.subscribe === 'function' ? options.subscribe(() => applyResolution()) : null;

      // mousedown: stop ProseMirror from moving the selection into the atom before our click.
      dom.addEventListener('mousedown', (e) => e.preventDefault());
      dom.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const id = applyResolution(); // re-resolve live (the title list may have loaded since render)
        if (id) options.onNavigate(id);
      });

      // --- Hover preview card (item 4): on a brief hover over a resolved link, show a small
      // card with the target note's title + a body snippet. Lightweight + cached by the caller.
      let hoverTimer: number | null = null;
      let card: HTMLDivElement | null = null;
      const removeCard = (): void => {
        if (card !== null) {
          card.remove();
          card = null;
        }
      };
      const showCard = (preview: { title: string; snippet: string }): void => {
        removeCard();
        card = document.createElement('div');
        card.className = 'wikilink-preview';
        const h = document.createElement('div');
        h.className = 'wikilink-preview__title';
        h.textContent = preview.title;
        card.appendChild(h);
        if (preview.snippet) {
          const s = document.createElement('div');
          s.className = 'wikilink-preview__snippet';
          s.textContent = preview.snippet;
          card.appendChild(s);
        }
        document.body.appendChild(card);
        const r = dom.getBoundingClientRect();
        card.style.left = `${Math.round(r.left)}px`;
        card.style.top = `${Math.round(r.bottom + 6)}px`;
      };
      dom.addEventListener('mouseenter', () => {
        const id = applyResolution();
        if (!id || typeof options.getPreview !== 'function') return;
        hoverTimer = window.setTimeout(() => {
          options
            .getPreview!(id)
            .then((p) => {
              if (p) showCard(p);
            })
            .catch(() => {
              /* preview is best-effort */
            });
        }, 350);
      });
      dom.addEventListener('mouseleave', () => {
        if (hoverTimer !== null) {
          window.clearTimeout(hoverTimer);
          hoverTimer = null;
        }
        removeCard();
      });

      return {
        dom,
        // Atom leaf — its text content is owned by us, not editable; ignore DOM mutations.
        ignoreMutation: () => true,
        destroy: () => {
          if (hoverTimer !== null) window.clearTimeout(hoverTimer);
          removeCard();
          if (unsubscribe !== null) unsubscribe();
        },
      };
    };
  },

  addInputRules() {
    // Typing the closing `]]` of `[[Title]]` converts the whole token into the chip immediately.
    return [
      nodeInputRule({
        find: WIKILINK_INPUT_RULE,
        type: this.type,
        getAttributes: (match) => ({ title: (match[1] ?? '').trim() }),
      }),
    ];
  },
});
